import { MAX_OPTION_COUNT, MIN_OPTION_COUNT, NOTE_NAMES, type NoteName } from '@cavaquinho/shared';
import type { MultipleChoiceConfig, NoteIdentificationConfig } from '../../lib/api/types.js';

/**
 * The client-side MIRROR of the server's configuration rules (T101; FR-041,
 * FR-050, SC-014).
 *
 * A mirror, and never the enforcement point. `apps/api`'s handler is
 * authoritative (Constitution § IV): a request that bypasses this file — a
 * script, a stale tab, a curl — is rejected identically by the server. What
 * this buys is immediacy: an administrator sees "escolha qual nota é a
 * correta" as they edit the pool, rather than after a round trip.
 *
 * The two are kept honest by sharing `@cavaquinho/shared`'s bounds rather
 * than restating them. The FIELD PATHS AND CODES below match
 * `contracts/admin-api.md` exactly, so a violation the server reports and one
 * this file predicts render through the same code path in the form — and a
 * divergence shows up as a field the form cannot place rather than as silently
 * different advice.
 *
 * EVERY violation is returned, never just the first (FR-050): an administrator
 * fixing a configuration should see the whole list.
 */
export interface FieldViolation {
  readonly path: string;
  readonly code: string;
  readonly message: string;
}

const field = (path: string, code: string, message: string): FieldViolation => ({
  path: path ? `config.${path}` : 'config',
  code,
  message,
});

export function validateNoteIdentificationConfig(
  config: Partial<NoteIdentificationConfig>,
): FieldViolation[] {
  const violations: FieldViolation[] = [];
  const { audioUrl, notePool, correctNote } = config;

  if (!audioUrl || audioUrl.trim() === '') {
    violations.push(field('audioUrl', 'too_small', 'Envie um arquivo de áudio.'));
  }

  const pool = Array.isArray(notePool) ? notePool.filter(isNote) : [];
  if (pool.length < MIN_OPTION_COUNT || pool.length > MAX_OPTION_COUNT) {
    violations.push(
      field(
        'notePool',
        'out_of_range',
        `Escolha entre ${MIN_OPTION_COUNT} e ${MAX_OPTION_COUNT} notas.`,
      ),
    );
  }

  if (!isNote(correctNote) || !pool.includes(correctNote)) {
    violations.push(field('correctNote', 'not_in_pool', 'Marque qual nota é a correta.'));
  }

  violations.push(...validateVisibleAnswers(config.visibleAnswers, pool.length));

  return violations;
}

/**
 * The client mirror of `multiple_choice`'s rules
 * (`apps/api/src/exercises/multiple-choice/config.ts`): a non-empty question,
 * 2-9 non-empty and distinct options, and a `correctIndex` that names one of
 * them.
 */
function validateQuestionOptions(config: {
  question?: string;
  options?: string[];
  correctIndex?: number;
  visibleAnswers?: number;
}): FieldViolation[] {
  const violations: FieldViolation[] = [];
  const question = config.question ?? '';
  if (question.trim() === '') {
    violations.push(field('question', 'too_small', 'Escreva a pergunta.'));
  }

  const options = Array.isArray(config.options) ? config.options : [];
  const trimmed = options.map((o) => o.trim());
  if (trimmed.some((o) => o === '')) {
    violations.push(field('options', 'too_small', 'Nenhuma opção pode ficar em branco.'));
  }
  if (options.length < MIN_OPTION_COUNT || options.length > MAX_OPTION_COUNT) {
    violations.push(
      field(
        'options',
        'out_of_range',
        `São necessárias entre ${MIN_OPTION_COUNT} e ${MAX_OPTION_COUNT} opções.`,
      ),
    );
  }
  const duplicates = trimmed.filter((o, i) => o !== '' && trimmed.indexOf(o) !== i);
  if (duplicates.length > 0) {
    violations.push(field('options', 'duplicate_options', 'As opções devem ser diferentes entre si.'));
  }

  const correctIndex = config.correctIndex;
  if (
    typeof correctIndex !== 'number' ||
    !Number.isInteger(correctIndex) ||
    correctIndex < 0 ||
    correctIndex >= options.length
  ) {
    violations.push(field('correctIndex', 'out_of_range', 'Marque qual opção é a correta.'));
  }

  violations.push(...validateVisibleAnswers(config.visibleAnswers, options.length));

  return violations;
}

export function validateMultipleChoiceConfig(config: Partial<MultipleChoiceConfig>): FieldViolation[] {
  return validateQuestionOptions(config);
}

/**
 * The mirror of `apps/api/src/exercises/visible-answers.ts`: absent shows
 * every answer; otherwise a whole number from the option floor up to the
 * answers there are.
 */
function validateVisibleAnswers(
  visibleAnswers: number | undefined,
  answerCount: number,
): FieldViolation[] {
  if (visibleAnswers === undefined) return [];
  if (
    !Number.isInteger(visibleAnswers) ||
    visibleAnswers < MIN_OPTION_COUNT ||
    visibleAnswers > MAX_OPTION_COUNT
  ) {
    return [
      field(
        'visibleAnswers',
        'out_of_range',
        `Mostre entre ${MIN_OPTION_COUNT} e ${MAX_OPTION_COUNT} respostas.`,
      ),
    ];
  }
  if (visibleAnswers > answerCount) {
    return [
      field(
        'visibleAnswers',
        'exceeds_pool_size',
        `Não é possível mostrar ${visibleAnswers} respostas: há apenas ${answerCount}.`,
      ),
    ];
  }
  return [];
}

function isNote(value: unknown): value is NoteName {
  return typeof value === 'string' && (NOTE_NAMES as readonly string[]).includes(value);
}

/** The violations for one field path, for rendering beneath its control. */
export function violationsFor(
  violations: readonly FieldViolation[],
  path: string,
): FieldViolation[] {
  return violations.filter((violation) => violation.path === path);
}

/**
 * What a deletion destroys and what it keeps, in the administrator's own
 * words (FR-062, FR-077's spirit applied to content).
 *
 * The counts come back from the server's own delete response, but this is the
 * text shown BEFORE confirming — so the numbers here are what the admin list
 * already knows, and the sentence's job is to be accurate about the KIND of
 * consequence: content goes, history does not.
 */
export interface DeletionImpact {
  readonly exerciseCount: number;
}

export function describeLessonDeletion({ exerciseCount }: DeletionImpact): string {
  const exercises =
    exerciseCount === 1 ? '1 exercício' : `${exerciseCount} exercícios`;
  return (
    `Esta lição e seus ${exercises} serão removidos do conteúdo disponível. ` +
    'O histórico de quem já praticou é permanente e não será apagado; ' +
    'quem estiver nesta lição continuará no próximo exercício disponível.'
  );
}

export function describeExerciseDeletion(): string {
  return (
    'Este exercício será removido do conteúdo disponível. ' +
    'As respostas já registradas são permanentes e não serão apagadas.'
  );
}
